"use client";

import { Map as MapIcon, Truck } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";

// Barra de aviso do topo: duas mensagens em carrossel (desliza para o lado a
// cada 5 s, em loop contínuo). Mesmo fundo, cores, fonte e altura de antes;
// cada mensagem cabe numa linha também em 320 px (no celular, a versão curta).
// Pausa com o mouse em cima ou com foco do teclado. Com "reduzir movimento"
// ligado no aparelho, troca sem animação.

const ROTATE_MS = 5000;
const SLIDE_MS = 600;

interface Announcement {
  id: string;
  icon: ReactNode;
  full: ReactNode;
  short: ReactNode;
}

const ANNOUNCEMENTS: Announcement[] = [
  {
    id: "frete",
    icon: <Truck size={16} className="shrink-0" aria-hidden="true" />,
    full: (
      <>
        <span className="text-secondary">Frete grátis</span> para Jundiaí e região. Consulte as regras!
      </>
    ),
    short: (
      <>
        <span className="text-secondary">Frete grátis</span> para Jundiaí e região
      </>
    ),
  },
  {
    id: "brasil",
    icon: <MapIcon size={16} className="shrink-0" aria-hidden="true" />,
    full: (
      <>
        Enviamos para <span className="text-secondary">todo o Brasil</span>
      </>
    ),
    short: (
      <>
        Enviamos para <span className="text-secondary">todo o Brasil</span>
      </>
    ),
  },
];

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

function Slide({ announcement, hidden }: { announcement: Announcement; hidden: boolean }) {
  return (
    <div
      className="flex w-full shrink-0 items-center justify-center gap-2 whitespace-nowrap"
      aria-hidden={hidden || undefined}
    >
      {announcement.icon}
      <span className="hidden sm:inline">{announcement.full}</span>
      <span className="sm:hidden">{announcement.short}</span>
    </div>
  );
}

export function TopAnnouncementBar() {
  // Posição no trilho [A, B, A(cópia)]: chegar na cópia e voltar ao 0 sem
  // animação dá o loop contínuo, sempre deslizando para o mesmo lado.
  const [position, setPosition] = useState(0);
  const [animate, setAnimate] = useState(true);
  const [paused, setPaused] = useState(false);
  const total = ANNOUNCEMENTS.length;

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setAnimate(!prefersReducedMotion());
      setPosition((current) => current + 1);
    }, ROTATE_MS);
    return () => window.clearInterval(timer);
  }, [paused]);

  useEffect(() => {
    if (position < total) return;
    // Chegou na cópia da primeira: volta para a original sem animação.
    const reset = window.setTimeout(() => {
      setAnimate(false);
      setPosition(0);
    }, animate ? SLIDE_MS : 0);
    return () => window.clearTimeout(reset);
  }, [position, total, animate]);

  const current = position % total;

  return (
    <div className="bg-primary-hover text-white">
      <div
        className="mx-auto max-w-7xl px-4 pb-1 pt-[max(0.25rem,env(safe-area-inset-top))] text-center text-xs font-semibold sm:text-sm"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
      >
        <div className="overflow-hidden" role="region" aria-roledescription="carrossel" aria-label="Avisos da loja">
          <div
            className="flex"
            style={{
              transform: `translateX(-${position * 100}%)`,
              transition: animate ? `transform ${SLIDE_MS}ms ease-in-out` : "none",
            }}
          >
            {ANNOUNCEMENTS.map((announcement, index) => (
              <Slide key={announcement.id} announcement={announcement} hidden={index !== current} />
            ))}
            <Slide key="loop" announcement={ANNOUNCEMENTS[0]} hidden />
          </div>
        </div>
      </div>
    </div>
  );
}
